// Frontend/src/api/payments.ts
/**
 * Payment & Invoice API client
 * Handles invoice creation, payment marking, and verification flows
 */

import api from "./client";

// ==================== TYPES ====================

export type InvoiceStatus =
    | "DRAFT"
    | "SENT"
    | "UNPAID"
    | "PARTIALLY_PAID"
    | "PENDING_VERIFICATION"
    | "PENDING_HOA_TRANSFER"
    | "PAID"
    | "CANCELLED";

export type PaymentMethod = "BANK_TRANSFER" | "ZELLE" | "STRIPE" | "PAGO_MOVIL" | "CASH";
export type PaymentFlow = "DIRECT_HOA" | "PLATFORM_RELAY";

export type NotificationType = "INVOICE_SENT" | "OVERDUE_REMINDER";
export type NotificationChannel = "EMAIL" | "SMS";
export type NotificationStatus = "SENT" | "FAILED" | "MOCKED";

export interface InvoiceNotification {
    id: string;
    invoice: string;
    /** Who it was about. The log used to carry only the invoice UUID. */
    invoice_period?: string | null;
    resident_name?: string | null;
    unit_code?: string | null;
    triggered_by?: string;
    triggered_by_name?: string;
    notification_type: NotificationType;
    channel: NotificationChannel;
    status: NotificationStatus;
    recipient_email: string;
    recipient_phone: string;
    sent_at: string;
    error_message: string;
    created_at: string;
}

export interface Invoice {
    id: string;
    resident: string;
    resident_name: string;
    resident_email: string;
    resident_phone?: string;
    unit: string;
    unit_code: string;
    building_id?: string;
    building_name?: string;
    building_code?: string;
    period: string;
    description: string;
    base_amount: string;
    credits: string;
    total_amount: string;
    amount_paid?: string;
    balance_due?: string;
    invoice_date: string;
    due_date: string;
    status: InvoiceStatus;
    payment_flow: PaymentFlow;
    payment_method?: PaymentMethod;
    hoa_payment_source?: "BUILDING" | "HOA_DEFAULT" | "MISSING" | null;
    hoa_payment_snapshot_at?: string;
    marked_paid_at?: string;
    marked_paid_by?: string;
    marked_paid_by_name?: string;
    verified_at?: string;
    verified_by?: string;
    verified_by_name?: string;
    forwarded_at?: string;
    forwarded_by?: string;
    forwarded_by_name?: string;
    finalized_at?: string;
    finalized_by?: string;
    finalized_by_name?: string;
    // Platform payment flow tracking
    paid_to_platform?: boolean;
    paid_to_platform_at?: string;
    paid_to_platform_amount?: string;
    platform_commission_percent?: string;
    platform_commission_amount?: string;
    paid_to_hoa?: boolean;
    paid_to_hoa_at?: string;
    paid_to_hoa_amount?: string;
    // Rejection tracking
    rejection_count?: number;
    last_rejected_at?: string;
    last_rejected_by?: string;
    last_rejected_by_name?: string;
    last_rejection_reason?: string;
    organization_id?: string;
    notes?: string;
    // Venezuela compliance
    bcv_rate_snapshot?: string;
    bcv_rate_date?: string;
    ves_total_amount?: string;
    resident_cedula?: string;
    building_rif?: string;
    has_payment_proof?: boolean;
    is_overdue?: boolean;
    payment_proofs?: PaymentProof[];
    notifications?: InvoiceNotification[];
    created_at: string;
    updated_at: string;
}

export interface PaymentProof {
    id: string;
    invoice: string;
    /** Per-payment ledger fields (partial / multi-month) */
    amount_usd?: string;
    amount_ves?: string;
    payment_date?: string;
    reference?: string;
    // The API serialises empty Django FileFields as null, not "".
    transaction_screenshot: string | null;
    id_verification: string | null;
    resident_notes?: string;
    admin_verification_notes?: string;
    admin_confirmation_file?: string | null;
    forwarding_proof?: string | null;
    forwarding_payment_method?: "BANK_TRANSFER" | "ZELLE" | "STRIPE";
    forwarding_notes?: string;
    uploaded_by?: string;
    uploaded_by_name?: string;
    /** BCV rate (VES/USD) at the moment the resident submitted this payment */
    bcv_rate_at_payment?: string;
    created_at: string;
    updated_at: string;
}

export interface CreateInvoiceData {
    resident: string | null;
    unit: string | null;
    period: string;
    description?: string;
    base_amount: number;
    credits?: number;
    due_date: string;
    status?: InvoiceStatus;
    payment_method?: PaymentMethod | null;
    notes?: string;
}

export interface BulkInvoiceData {
    resident_ids: string[];
    period: string;
    description?: string;
    use_unit_fee?: boolean;
    fixed_amount?: number;
    credits?: number;
    due_date: string;
    payment_method?: PaymentMethod;
    send_now?: boolean;
    notes?: string;
    tenant_only?: boolean;
}

export interface BulkInvoiceResult {
    success: boolean;
    created: number;
    skipped_existing: number;
    skipped_no_fee: number;
    skipped_inactive: number;
    invoice_ids: string[];
}

export interface MarkAsPaidData {
    payment_method: PaymentMethod;
    transaction_screenshot: File;
    id_verification: File;
    resident_notes?: string;
    /** Date the transfer actually left the resident's account (YYYY-MM-DD).
     *  The rate the payment is recorded at comes from this day, not from the
     *  day the proof was uploaded. */
    payment_date?: string;
}

export interface VerifyPaymentData {
    admin_verification_notes?: string;
    admin_confirmation_file?: File;
    approve: boolean;
}

export interface ForwardToHOAData {
    payment_method: "BANK_TRANSFER" | "ZELLE" | "STRIPE" | "PAGO_MOVIL" | "CASH";
    forwarding_proof: File;
    forwarding_notes?: string;
}

export interface FinalizePaymentData {
    notes?: string;
}

export interface RecordCashPaymentData {
    notes?: string;
}

export type ManualPaymentMethod = "CASH" | "BANK_TRANSFER";

export interface RecordManualPaymentData {
    payment_method: ManualPaymentMethod;
    /** Actual date the payment was received (YYYY-MM-DD) */
    payment_date: string;
    /** USD amount applied to the invoice balance */
    amount_usd?: number | string;
    /** VES amount received (for the record / dual-currency display) */
    amount_ves?: number | string;
    notes?: string;
    reference?: string;
    transaction_screenshot?: File | null;
}

// ==================== API FUNCTIONS ====================

/**
 * HOA ADMIN ENDPOINTS
 */

// Get all invoices (filtered by organization)
export async function getInvoices(): Promise<Invoice[]> {
    const response = await api.get("/building/payments/invoices/");
    return response.data;
}

// Get single invoice details
export async function getInvoice(id: string): Promise<Invoice> {
    const response = await api.get(`/building/payments/invoices/${id}/`);
    return response.data;
}

// Create a single invoice (HOA Admin)
export async function createInvoice(data: CreateInvoiceData): Promise<Invoice> {
    const response = await api.post("/building/payments/invoices/", data);
    return response.data;
}

// Delete invoice (HOA Admin only - DRAFT or CANCELLED invoices only)
export async function deleteInvoice(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/building/payments/invoices/${id}/`);
    return response.data;
}

// Bulk create invoices (HOA Admin)
export async function bulkCreateInvoices(
    data: BulkInvoiceData
): Promise<BulkInvoiceResult> {
    const response = await api.post(
        "/building/payments/invoices/bulk_create/",
        data
    );
    return response.data;
}

// Get building options for invoice creation (HOA Admin)
export async function getBuildingOptions(): Promise<{
    buildings: Array<{
        id: string;
        name: string;
        address: string;
        city: string;
    }>;
}> {
    const response = await api.get(
        "/building/payments/invoices/building_options/"
    );
    return response.data;
}

// Get residents by building for invoice creation (HOA Admin)
export async function getResidentsByBuilding(buildingId: string): Promise<{
    residents: Array<{
        id: string;
        full_name: string;
        email: string;
        phone: string;
        role: string;
        role_display: string;
        unit_id: string | null;
        unit_code: string | null;
        unit_building: string;
        monthly_fee: string | null;
    }>;
}> {
    const response = await api.get(
        `/building/payments/invoices/residents_by_building/?building_id=${buildingId}`
    );
    return response.data;
}

// Get units by building for invoice creation (HOA Admin)
export async function getUnitsByBuilding(buildingId: string): Promise<{
    units: Array<{
        id: string;
        unit_code: string;
        floor: string;
        number: string;
        monthly_fee: string;
        status: string;
        status_display: string;
        resident: {
            id: string;
            full_name: string;
            email: string;
            role: string;
            role_display: string;
        } | null;
    }>;
}> {
    const response = await api.get(
        `/building/payments/invoices/units_by_building/?building_id=${buildingId}`
    );
    return response.data;
}

// Search residents for invoice creation (HOA Admin)
export async function searchResidents(
    query: string,
    buildingId?: string
): Promise<{
    residents: Array<{
        id: string;
        full_name: string;
        email: string;
        phone: string;
        role: string;
        role_display: string;
        unit_id: string | null;
        unit_code: string | null;
        unit_building: string;
        monthly_fee: string | null;
    }>;
}> {
    const params = new URLSearchParams({ q: query });
    if (buildingId) params.append("building_id", buildingId);
    const response = await api.get(
        `/building/payments/invoices/search_residents/?${params.toString()}`
    );
    return response.data;
}

// Get unpaid invoices
export async function getUnpaidInvoices(): Promise<Invoice[]> {
    const response = await api.get("/building/payments/invoices/unpaid/");
    return response.data;
}

// Get invoices pending HOA confirmation (forwarded by Super Admin but not finalized)
export async function getPendingConfirmationInvoices(): Promise<Invoice[]> {
    const response = await api.get("/building/payments/invoices/pending_confirmation/");
    return response.data;
}

// Finalize payment (HOA Admin - after super admin verification)
export async function finalizePayment(
    id: string,
    data: FinalizePaymentData
): Promise<Invoice> {
    const response = await api.post(
        `/building/payments/invoices/${id}/finalize_payment/`,
        data
    );
    return response.data;
}

// Record a cash payment directly by HOA admin (bypasses super-admin flow)
export async function recordCashPayment(
    id: string,
    data: RecordCashPaymentData
): Promise<Invoice> {
    const response = await api.post(
        `/building/payments/invoices/${id}/record-cash-payment/`,
        data
    );
    return response.data;
}

// Record a manual payment (cash or bank transfer) directly by HOA admin.
// Bank transfers require a transaction proof file → sent as multipart/form-data.
export async function recordManualPayment(
    id: string,
    data: RecordManualPaymentData
): Promise<Invoice> {
    const form = new FormData();
    form.append("payment_method", data.payment_method);
    form.append("payment_date", data.payment_date);
    if (data.amount_usd !== undefined && data.amount_usd !== "") {
        form.append("amount_usd", String(data.amount_usd));
    }
    if (data.amount_ves !== undefined && data.amount_ves !== "") {
        form.append("amount_ves", String(data.amount_ves));
    }
    if (data.notes) form.append("notes", data.notes);
    if (data.reference) form.append("reference", data.reference);
    if (data.transaction_screenshot) {
        form.append("transaction_screenshot", data.transaction_screenshot);
    }

    const response = await api.post(
        `/building/payments/invoices/${id}/record-manual-payment/`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
}

/**
 * RESIDENT ENDPOINTS
 */

// Get resident's own invoices
export async function getMyInvoices(): Promise<Invoice[]> {
    const response = await api.get("/building/payments/invoices/my_invoices/");
    return response.data;
}

// Get invoices across all units for multi-unit residents
export async function getAllMyInvoices(): Promise<Invoice[]> {
    const response = await api.get("/building/payments/invoices/all-units/");
    return response.data;
}

// Mark invoice as paid (resident uploads proof)
export async function markInvoiceAsPaid(
    id: string,
    data: MarkAsPaidData
): Promise<Invoice> {
    const formData = new FormData();
    formData.append("payment_method", data.payment_method);
    formData.append("transaction_screenshot", data.transaction_screenshot);
    formData.append("id_verification", data.id_verification);
    if (data.resident_notes) {
        formData.append("resident_notes", data.resident_notes);
    }
    // The API has accepted this since M1; until now nothing sent it, so every
    // resident payment was recorded at the UPLOAD day's rate. Someone who
    // paid on Friday and uploaded on Monday was converted at Monday's rate.
    if (data.payment_date) {
        formData.append("payment_date", data.payment_date);
    }

    const response = await api.post(
        `/building/payments/invoices/${id}/mark_as_paid/`,
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }
    );
    return response.data;
}

/**
 * SUPER ADMIN ENDPOINTS
 */

// Get all invoices pending verification
export async function getPendingVerificationInvoices(buildingId?: string): Promise<Invoice[]> {
    const response = await api.get(
        "/building/payments/invoices/pending_verification/",
        buildingId ? { params: { building_id: buildingId } } : undefined
    );
    return response.data;
}

// Get all invoices pending HOA transfer (verified but not forwarded)
export async function getPendingHOATransferInvoices(buildingId?: string): Promise<Invoice[]> {
    const response = await api.get(
        "/building/payments/invoices/pending_hoa_transfer/",
        buildingId ? { params: { building_id: buildingId } } : undefined
    );
    return response.data;
}

// Get all invoices awaiting HOA confirmation (forwarded but not finalized)
export async function getAwaitingHOAConfirmation(buildingId?: string): Promise<Invoice[]> {
    const response = await api.get(
        "/building/payments/invoices/awaiting_hoa_confirmation/",
        buildingId ? { params: { building_id: buildingId } } : undefined
    );
    return response.data;
}

// Get all verified/approved payments (history)
export async function getVerifiedPaymentsHistory(buildingId?: string): Promise<Invoice[]> {
    const response = await api.get(
        "/building/payments/invoices/verified_payments/",
        buildingId ? { params: { building_id: buildingId } } : undefined
    );
    return response.data;
}

// Verify payment (super admin approves/rejects with proof)
export async function verifyPayment(
    id: string,
    data: VerifyPaymentData
): Promise<Invoice> {
    const formData = new FormData();
    if (data.admin_verification_notes) {
        formData.append(
            "admin_verification_notes",
            data.admin_verification_notes
        );
    }
    if (data.admin_confirmation_file) {
        formData.append(
            "admin_confirmation_file",
            data.admin_confirmation_file
        );
    }
    formData.append("approve", data.approve.toString());

    const response = await api.post(
        `/building/payments/invoices/${id}/verify_payment/`,
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }
    );
    return response.data;
}

// Forward payment to HOA (super admin uploads proof of transfer)
export async function forwardToHOA(
    id: string,
    data: ForwardToHOAData
): Promise<Invoice> {
    console.log("forwardToHOA called with data:", data);
    const formData = new FormData();
    formData.append("payment_method", data.payment_method);
    formData.append("forwarding_proof", data.forwarding_proof);
    if (data.forwarding_notes) {
        formData.append("forwarding_notes", data.forwarding_notes);
    }
    
    // Log FormData contents
    console.log("FormData contents:");
    for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
    }

    const response = await api.post(
        `/building/payments/invoices/${id}/forward_to_hoa/`,
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }
    );
    return response.data;
}

/**
 * PAYMENT PROOFS
 */

// Get all payment proofs (filtered by role)
export async function getPaymentProofs(): Promise<PaymentProof[]> {
    const response = await api.get("/building/payments/proofs/");
    return response.data;
}

// Get single payment proof
export async function getPaymentProof(id: string): Promise<PaymentProof> {
    const response = await api.get(`/building/payments/proofs/${id}/`);
    return response.data;
}

/**
 * BCV EXCHANGE RATE
 */

export interface BCVRate {
    dollar: number;
    /** The value date (fecha valor) this rate applies to, not the scrape date. */
    date: string;
    source: string;
    fetched_at?: string;
    /** False when BCV published nothing that day and this was carried forward. */
    is_exact?: boolean;
    days_carried?: number;
    is_stale?: boolean;
    source_kind?: string;
}

/**
 * Get the official BCV rate for a banking day.
 *
 * Pass the payment date when recording a back-dated payment. Without it the
 * caller gets today's rate, which is how a payment made last Friday came to
 * be converted at this Monday's number.
 *
 * Throws on 404 — the requested day predates our rate history, and there is
 * no honest answer to convert with.
 */
export async function getBCVRate(date?: string): Promise<BCVRate> {
    const response = await api.get("/building/payments/bcv-rate/", {
        params: date ? { date } : undefined,
    });
    return response.data;
}

export type BCVRateDay = {
    date: string;
    /** ISO weekday, 1 = Monday. 6 and 7 are provably the weekend. */
    weekday: number;
    rate: string | null;
    is_published: boolean;
    carried_from: string | null;
    days_carried: number | null;
    /** null when published. Never "HOLIDAY" — see the endpoint's docstring. */
    reason: "WEEKEND" | "NO_PUBLICATION" | "BEFORE_COVERAGE" | null;
    source_kind: string | null;
    change: string | null;
    change_pct: string | null;
};

export type BCVRateHistory = {
    month: string;
    days: BCVRateDay[];
    summary: {
        published: number;
        carried: number;
        uncovered: number;
        longest_weekday_gap: number;
        coverage_starts: string | null;
        today_is_stale: boolean;
        today_days_carried: number | null;
    };
};

/**
 * Every calendar day of a month with the rate that was in force, and how it
 * was arrived at. A list of only the days BCV published cannot answer "what
 * rate was used on the 24th" when BCV published nothing that day.
 */
export async function getBCVRateHistory(month: string): Promise<BCVRateHistory> {
    const response = await api.get("/building/payments/bcv-rate/history/", {
        params: { month },
    });
    return response.data;
}

/**
 * Today in America/Caracas, as YYYY-MM-DD.
 *
 * NOT `new Date().toISOString().slice(0, 10)`: toISOString is UTC, and
 * Caracas is UTC-4, so between 20:00 and midnight local that returns
 * TOMORROW. A date input defaulted that way offers a payment date in the
 * future, which the server then rejects.
 */
export function todayInCaracas(): string {
    return new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Caracas",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(new Date());
}

// Download invoice/receipt PDF
export async function downloadInvoice(id: string): Promise<Blob> {
    const response = await api.get(`/building/payments/invoices/${id}/download/`, {
        responseType: "blob",
    });
    return response.data;
}

// Send overdue reminder for an invoice
export async function sendOverdueReminder(id: string): Promise<InvoiceNotification[]> {
    const response = await api.post<InvoiceNotification[]>(
        `/building/payments/invoices/${id}/send_reminder/`
    );
    return response.data;
}

// Bulk: send overdue reminders to all eligible invoices at once
export interface BulkReminderResult {
    total: number;
    sent: number;
    mocked: number;
    failed: number;
    skipped: number;
    details: {
        invoice_id: string;
        resident: string;
        unit: string;
        period: string;
        days_overdue: number;
        status: "SENT" | "MOCKED" | "FAILED" | "ERROR";
    }[];
}

/** One row of "who would be reminded", for the confirmation dialog. */
export interface OverdueReminderCandidate {
    invoice_id: string;
    resident: string;
    email: string;
    unit: string;
    period: string;
    due_date: string;
    days_overdue: number;
    balance_due: string;
}

/**
 * Who WOULD be reminded, without reminding them.
 *
 * The eligibility rule is six conditions deep and three of them are about
 * payment activity rather than status, so this asks the server rather than
 * reproducing it here and drifting.
 */
export async function getOverdueReminderCandidates(): Promise<OverdueReminderCandidate[]> {
    const response = await api.get<OverdueReminderCandidate[]>(
        `/building/payments/invoices/overdue_reminder_candidates/`
    );
    return response.data;
}

/**
 * Send the reminders. Pass `invoiceIds` to send to a subset.
 *
 * The server intersects the ids with its own eligible set, so a stale dialog
 * cannot mail someone who has since paid. Omitting the argument keeps the
 * original "send to everyone" behaviour.
 */
export async function bulkSendReminders(
    invoiceIds?: string[]
): Promise<BulkReminderResult> {
    const response = await api.post<BulkReminderResult>(
        `/building/payments/invoices/bulk_send_reminders/`,
        invoiceIds ? { invoice_ids: invoiceIds } : {}
    );
    return response.data;
}

// Fetch the notification delivery log (all notifications for admin's org)
export async function getNotificationLog(invoiceId?: string): Promise<InvoiceNotification[]> {
    const params = invoiceId ? { invoice: invoiceId } : {};
    const response = await api.get<InvoiceNotification[]>(`/notifications/`, { params });
    return response.data;
}

/**
 * PAYMENT DETAILS (HOA Admin & Super Admin)
 */

export interface PaymentDetails {
    id: string | null;
    user_email: string | null;
    user_id?: string | null;
    bank_name?: string;
    bank_account_number?: string;
    bank_routing_number?: string;
    account_holder_name?: string;
    zelle_email?: string;
    zelle_phone?: string;
    paypal_email?: string;
    venmo_username?: string;
    cashapp_username?: string;
    pago_movil_bank?: string;
    pago_movil_phone?: string;
    pago_movil_id_number?: string;
    cash_instructions?: string;
    is_verified: boolean;
    created_at: string;
    updated_at: string;
}

export interface InvoicePaymentDetails {
    platform_payment_details: PaymentDetails | null;
    resident_payment_details: PaymentDetails | null;
    resident_payment_source?: "BUILDING" | "HOA_DEFAULT" | "MISSING" | null;
    resident_payment_config_status?: "CONFIGURED" | "HOA_DEFAULT" | "MISSING" | "SNAPSHOT" | null;
    hoa_payment_details: PaymentDetails | null;
    hoa_admin_id: string | null;
    hoa_admin_email: string | null;
    building_id?: string | null;
    building_name?: string | null;
    building_code?: string | null;
    hoa_payment_source?: "BUILDING" | "HOA_DEFAULT" | "MISSING" | null;
    hoa_payment_config_status?: "CONFIGURED" | "HOA_DEFAULT" | "MISSING" | "SNAPSHOT" | null;
    hoa_payment_config_id?: string | null;
    hoa_payment_snapshot_at?: string | null;
    hoa_payment_details_from_snapshot?: boolean;
}

// Get payment details for a specific invoice (platform + HOA admin)
export async function getInvoicePaymentDetails(
    invoiceId: string
): Promise<InvoicePaymentDetails> {
    const response = await api.get(
        `/building/payments/invoices/${invoiceId}/payment_details/`
    );
    return response.data;
}

/** Trigger a reminder email to the HOA admin to set up their payment method. */
export async function remindHoaPaymentSetup(
    invoiceId: string
): Promise<{ detail: string; sent_to: string }> {
    const response = await api.post(
        `/building/payments/invoices/${invoiceId}/remind_hoa_payment_setup/`
    );
    return response.data;
}

// Get all payment details (admin only)
export async function getPaymentDetailsList(): Promise<PaymentDetails[]> {
    const response = await api.get("/auth/payment-details/");
    return response.data;
}

// Get specific payment details
export async function getPaymentDetailsById(id: string): Promise<PaymentDetails> {
    const response = await api.get(`/auth/payment-details/${id}/`);
    return response.data;
}

// Create payment details
export async function createPaymentDetails(
    data: Partial<PaymentDetails>
): Promise<PaymentDetails> {
    const response = await api.post("/auth/payment-details/", data);
    return response.data;
}

// Update payment details
export async function updatePaymentDetails(
    id: string,
    data: Partial<PaymentDetails>
): Promise<PaymentDetails> {
    const response = await api.patch(`/auth/payment-details/${id}/`, data);
    return response.data;
}

/** Retry a failed notification — returns newly-created notification records. */
export async function resendNotification(notificationId: string): Promise<InvoiceNotification[]> {
    const response = await api.post<InvoiceNotification[]>(
        `/notifications/${notificationId}/resend/`
    );
    return response.data;
}
